import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { LicenseTypeLabels } from "../../constants/licenseTypes";
import { useNotification } from "../../contexts/NotificationContext";
import { userProductHardwareService } from "../../services/userProductHardwareService";
import { userProductLicenseService } from "../../services/userProductLicenseService";
import { isLicenseServerAssignment } from "../../utils/licenseServerUtils";
import {
  calculateLicenseStatus,
  LicenseStatusConfig,
} from "../../utils/licenseStatusUtils";

const validationSchema = Yup.object({
  hardwareId: Yup.string()
    .trim()
    .max(255)
    .matches(/^[A-Za-z0-9+/=]+$/, "Enter a valid hardware ID")
    .required("Hardware ID is required"),
  deviceName: Yup.string().trim().max(255),
});

function LicenseDetails({ license, onCopy, busy }) {
  if (!license) return null;
  const status = LicenseStatusConfig[calculateLicenseStatus(license)];
  return (
    <Box component="details" sx={{ mt: 1 }}>
      <Typography
        component="summary"
        sx={{ cursor: "pointer", color: "primary.main" }}
      >
        View license details
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        <Typography variant="body2">
          Status: {status.label} | Expiry:{" "}
          {license.expiry_date
            ? new Date(license.expiry_date).toLocaleDateString()
            : "Not recorded"}
        </Typography>
        <Typography variant="body2">
          Type: {LicenseTypeLabels[license.license_type] || "Not recorded"}
        </Typography>
        <Typography variant="body2">
          Created:{" "}
          {license.created_at
            ? new Date(license.created_at).toLocaleString()
            : "Not recorded"}
        </Typography>
        <Typography variant="body2">
          Last updated:{" "}
          {license.updated_at
            ? new Date(license.updated_at).toLocaleString()
            : "Not recorded"}
        </Typography>
        {license.license_key ? (
          <>
            <TextField
              label="License key"
              value={license.license_key}
              fullWidth
              multiline
              slotProps={{
                input: {
                  readOnly: true,
                  sx: {
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  },
                },
              }}
              sx={{ fontFamily: "monospace", fontSize: "14px" }}
            />
            <Button
              disabled={busy}
              onClick={() => onCopy(license.license_key)}
              sx={{ alignSelf: "flex-start" }}
            >
              Copy license key
            </Button>
          </>
        ) : (
          <Typography variant="body2">
            No saved key. Generate a license for the approved hardware ID.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default function UserProductHardwareIds({
  user,
  onGenerateLicense,
  onHardwareUpdated,
}) {
  const [submissions, setSubmissions] = useState([]);
  const [licenses, setLicenses] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const { showNotification } = useNotification();
  const products = (user?.products || []).filter(
    (product) => product.requires_license,
  );

  async function fetchData() {
    const [hardware, licenseEntries] = await Promise.all([
      userProductHardwareService.getUserHardwareSubmissions(user.id),
      Promise.all(
        products
          .filter((product) => !isLicenseServerAssignment(product))
          .map(async (product) => {
            const response = await userProductLicenseService.getLicenseHistory(
              user.id,
              product.id,
            );
            return [product.id, response.licenses || []];
          }),
      ),
    ]);
    return {
      hardware: hardware.submissions || [],
      licenses: Object.fromEntries(licenseEntries),
    };
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    setEditing(null);
    setError("");
    fetchData()
      .then((data) => {
        if (active) {
          setSubmissions(data.hardware);
          setLicenses(data.licenses);
        }
      })
      .catch((err) => {
        if (active)
          setError(
            err.response?.data?.message ||
              err.message ||
              "Failed to load hardware and licenses",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user]);

  async function refresh() {
    const data = await fetchData();
    setSubmissions(data.hardware);
    setLicenses(data.licenses);
  }

  async function run(action) {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Request failed");
    } finally {
      setBusy(false);
    }
  }

  async function saveHardwareId(values, { setSubmitting }) {
    await run(async () => {
      await userProductHardwareService.updateHardwareId(
        user.id,
        editing.productId,
        {
          hardwareId: values.hardwareId.trim(),
          deviceName: values.deviceName.trim(),
          submissionId: editing.submissionId,
        },
      );
      setEditing(null);
      await refresh();
      await onHardwareUpdated?.();
      showNotification(
        "Hardware ID saved and approved. If the ID changed, generate a new license.",
        "success",
      );
    });
    setSubmitting(false);
  }

  async function review(submission, approve) {
    if (
      !approve &&
      !window.confirm(
        "Reject this hardware ID? The submission will be removed and the user can submit another.",
      )
    )
      return;
    await run(async () => {
      await userProductHardwareService.confirmHardwareId(
        submission.id,
        approve,
        submission,
      );
      await refresh();
      await onHardwareUpdated?.();
      showNotification(
        approve ? "Hardware ID approved" : "Hardware ID rejected",
        "success",
      );
    });
  }

  const copyKey = (key) =>
    run(async () => {
      await navigator.clipboard.writeText(key);
      showNotification("License key copied", "success");
    });

  const editor = (submission) => (
    <Formik
      initialValues={{
        hardwareId: submission?.hardware_id || "",
        deviceName: submission?.device_name || "",
      }}
      validationSchema={validationSchema}
      onSubmit={saveHardwareId}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form>
          <Field
            as={TextField}
            name="hardwareId"
            label="Hardware ID"
            fullWidth
            multiline
            error={touched.hardwareId && Boolean(errors.hardwareId)}
            helperText={touched.hardwareId && errors.hardwareId}
          />
          <Field
            as={TextField}
            name="deviceName"
            label="Device name / host name (optional)"
            fullWidth
            margin="normal"
            error={touched.deviceName && Boolean(errors.deviceName)}
            helperText={touched.deviceName && errors.deviceName}
          />
          <Stack direction="row" spacing={1}>
            <Button
              type="submit"
              variant="contained"
              disabled={busy || isSubmitting}
            >
              Save and approve
            </Button>
            <Button
              disabled={busy || isSubmitting}
              onClick={() => setEditing(null)}
            >
              Cancel
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  );

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Hardware &amp; Licenses</Typography>
        <Typography variant="body2" color="text.secondary">
          Manage hardware approval and a separate license for each device,
          grouped by product.
        </Typography>
      </Box>
      <Alert severity="info">
        Changing a hardware ID clears only that device's saved license key.
        Generate a new key after saving. Updated assignment terms apply when
        generating a license.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
      <Button
        disabled={busy || loading}
        onClick={() => run(refresh)}
        sx={{ alignSelf: "flex-start" }}
      >
        Refresh hardware and licenses
      </Button>
      {loading ? (
        <CircularProgress />
      ) : products.length === 0 ? (
        <Alert severity="info">
          No products requiring a license are assigned to this user.
        </Alert>
      ) : (
        products.map((product) => {
          const assignment = product.UserProduct || product.user_products || {};
          const devices = submissions.filter(
            (row) => row.product_id === product.id,
          );
          const savedLicenses = licenses[product.id] || [];
          const oldLicenses = savedLicenses.filter(
            (license) =>
              !devices.some(
                (device) => device.hardware_id === license.hardware_id,
              ),
          );
          return (
            <Accordion
              key={`${user.id}-${product.id}`}
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id={`product-${user.id}-${product.id}-header`}
                aria-controls={`product-${user.id}-${product.id}-details`}
              >
                <Box sx={{ minWidth: 0, py: 0.5 }}>
                  <Typography
                    component="span"
                    variant="h6"
                    sx={{ display: "block", overflowWrap: "anywhere" }}
                  >
                    {product.name}
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {isLicenseServerAssignment(product) ? (
                      "License server assignment"
                    ) : (
                      <>
                        {devices.length} / {assignment.license_limit || 1}{" "}
                        hardware IDs submitted | Assigned type:{" "}
                        {LicenseTypeLabels[assignment.license_type] ||
                          "Not configured"}{" "}
                        | License time: {assignment.license_months ?? 1}{" "}
                        month(s)
                      </>
                    )}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {isLicenseServerAssignment(product) ? (
                  <Alert severity="info">
                    License server assignment: hardware IDs and generated device
                    licenses are not required.
                  </Alert>
                ) : (
                  <>
                    <Stack spacing={2}>
                      {devices.map((submission) => {
                        const deviceLicenses = savedLicenses.filter(
                          (license) =>
                            license.hardware_id === submission.hardware_id,
                        );
                        const license = deviceLicenses[0];
                        const status =
                          LicenseStatusConfig[calculateLicenseStatus(license)];
                        const approved =
                          submission.is_confirmed && submission.is_locked;
                        const isEditing =
                          editing?.productId === product.id &&
                          editing?.submissionId === submission.id;
                        return (
                          <Box
                            key={submission.id}
                            sx={{
                              bgcolor: "action.hover",
                              borderRadius: 1,
                              p: 2,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              useFlexGap
                              flexWrap="wrap"
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              <Typography fontWeight="medium">
                                {submission.device_name || "Unnamed device"}
                              </Typography>
                              <Chip
                                size="small"
                                label={
                                  approved
                                    ? "Hardware approved"
                                    : submission.is_locked
                                      ? "Hardware locked"
                                      : "Pending approval"
                                }
                                color={approved ? "success" : "warning"}
                              />
                              <Chip
                                size="small"
                                label={status.label}
                                color={status.color}
                              />
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                overflowWrap: "anywhere",
                                mb: 1,
                              }}
                            >
                              {submission.hardware_id}
                            </Typography>
                            {isEditing ? (
                              editor(submission)
                            ) : (
                              <>
                                {license && (
                                  <Typography variant="body2">
                                    License expiry:{" "}
                                    {license.expiry_date
                                      ? new Date(
                                          license.expiry_date,
                                        ).toLocaleDateString()
                                      : "Not recorded"}
                                  </Typography>
                                )}
                                <LicenseDetails
                                  license={license}
                                  onCopy={copyKey}
                                  busy={busy}
                                />
                                {deviceLicenses.length > 1 && (
                                  <Box component="details" sx={{ mt: 1 }}>
                                    <Typography
                                      component="summary"
                                      sx={{ cursor: "pointer" }}
                                    >
                                      Other saved licenses (
                                      {deviceLicenses.length - 1})
                                    </Typography>
                                    {deviceLicenses.slice(1).map((record) => (
                                      <LicenseDetails
                                        key={record.id}
                                        license={record}
                                        onCopy={copyKey}
                                        busy={busy}
                                      />
                                    ))}
                                  </Box>
                                )}
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  useFlexGap
                                  flexWrap="wrap"
                                  sx={{ mt: 2 }}
                                >
                                  {!submission.is_confirmed &&
                                    !submission.is_locked && (
                                      <>
                                        <Button
                                          disabled={busy || !!editing}
                                          onClick={() =>
                                            review(submission, true)
                                          }
                                        >
                                          Approve hardware
                                        </Button>
                                        <Button
                                          color="error"
                                          disabled={busy || !!editing}
                                          onClick={() =>
                                            review(submission, false)
                                          }
                                        >
                                          Reject hardware
                                        </Button>
                                      </>
                                    )}
                                  <Button
                                    disabled={busy || !!editing}
                                    onClick={() =>
                                      setEditing({
                                        productId: product.id,
                                        submissionId: submission.id,
                                      })
                                    }
                                  >
                                    Edit hardware ID
                                  </Button>
                                  <Button
                                    variant="contained"
                                    disabled={busy || !!editing || !approved}
                                    onClick={() =>
                                      onGenerateLicense(
                                        user.id,
                                        product,
                                        submission.hardware_id,
                                      )
                                    }
                                  >
                                    {license?.license_key
                                      ? "Regenerate license"
                                      : "Generate license"}
                                  </Button>
                                </Stack>
                                {!approved && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Approve the hardware ID before generating a
                                    license.
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                    {devices.length === 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                          No hardware IDs submitted. The user can submit devices
                          up to the assigned limit.
                        </Typography>
                        {editing?.productId === product.id ? (
                          editor(null)
                        ) : (
                          <Button
                            disabled={busy || !!editing}
                            onClick={() =>
                              setEditing({ productId: product.id })
                            }
                          >
                            Set hardware ID
                          </Button>
                        )}
                      </Box>
                    )}
                    {oldLicenses.length > 0 && (
                      <Box component="details" sx={{ mt: 2 }}>
                        <Typography
                          component="summary"
                          sx={{ cursor: "pointer" }}
                        >
                          Saved licenses for previous hardware IDs (
                          {oldLicenses.length})
                        </Typography>
                        {oldLicenses.map((record) => (
                          <Box
                            key={record.id}
                            sx={{ mt: 1, overflowWrap: "anywhere" }}
                          >
                            <Typography variant="body2">
                              Hardware ID:{" "}
                              {record.hardware_id || "Not recorded"}
                            </Typography>
                            <Typography variant="body2">
                              Status:{" "}
                              {
                                LicenseStatusConfig[
                                  calculateLicenseStatus(record)
                                ].label
                              }{" "}
                              | Expiry:{" "}
                              {record.expiry_date
                                ? new Date(
                                    record.expiry_date,
                                  ).toLocaleDateString()
                                : "Not recorded"}
                            </Typography>
                            <LicenseDetails
                              license={record}
                              onCopy={copyKey}
                              busy={busy}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Stack>
  );
}
